import React, { useMemo } from 'react';
import { isGoodNight } from '@/utils/visibilityBands';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { MapPin, Calendar, Edit2, Trash2, Star } from 'lucide-react';
import { TravelStop } from '@/hooks/useTravelItineraries';
import { BortleBadge } from '@/components/locations/BortleBadge';
import { format, parseISO, eachDayOfInterval, subDays } from 'date-fns';
import { calculateMonthData, DayData, getVisibilityRating } from '@/utils/calendarData';
import { TripCalendarTable } from './TripCalendarTable';
import { TripStopWeather } from '@/components/weather/TripStopWeather';
import { getShootingQualityScore, getDarknessWindowMinutes, getQualityLabel } from '@/utils/shootingQualityScore';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

interface TripStopCardProps {
  stop: TravelStop;
  stopNumber: number;
  isLast: boolean;
  onEdit: () => void;
  onDelete: () => void;
  bortleClass?: number | null;
}


export const TripStopCard: React.FC<TripStopCardProps> = ({
  stop,
  stopNumber,
  isLast,
  onEdit,
  onDelete,
  bortleClass
}) => {
  const arrivalDate = parseISO(stop.arrival_date);
  const departureDate = parseISO(stop.departure_date);

  // Calculate visibility data for each night of the stay
  const nightsData = useMemo(() => {
    const location = {
      name: stop.location_name,
      latitude: Number(stop.latitude),
      longitude: Number(stop.longitude)
    };

    const lastNight = subDays(departureDate, 1);
    const days = arrivalDate <= lastNight ? eachDayOfInterval({ start: arrivalDate, end: lastNight }) : [];
    const results: Array<{ date: Date; dayData: DayData | null; rating: number }> = [];

    // Pre-compute unique months to avoid redundant calculateMonthData calls
    const monthCache = new Map<string, DayData[]>();
    days.forEach(day => {
      const key = `${day.getMonth()}-${day.getFullYear()}`;
      if (!monthCache.has(key)) {
        try {
          monthCache.set(key, calculateMonthData(location, day.getMonth(), day.getFullYear()));
        } catch (err) {
          monthCache.set(key, []);
        }
      }
    });

    days.forEach(day => {
      const key = `${day.getMonth()}-${day.getFullYear()}`;
      const monthData = monthCache.get(key) || [];
      const dayData = monthData.find(d => d.date === day.getDate());
      const rating = dayData ? getVisibilityRating(dayData) : 0;
      results.push({ date: day, dayData: dayData || null, rating });
    });

    return results;
  }, [stop, arrivalDate, departureDate]);

  const bestNight = nightsData.reduce((best, current) =>
    current.rating > best.rating ? current : best
  , nightsData[0]);

  const bestNightQuality = bestNight?.dayData ? getShootingQualityScore({
    mwVisibilityRating: bestNight.rating,
    maxElevation: bestNight.dayData.maxElevation,
    darknessWindowMinutes: getDarknessWindowMinutes(
      bestNight.dayData.astroTwilightEvening,
      bestNight.dayData.astroTwilightMorning
    ),
    bortleClass: bortleClass ?? undefined,
  }) : 0;

  const totalNights = nightsData.length;
  const goodNights = nightsData.filter(n => isGoodNight(n.rating)).length;

  return (
    <div className="relative pl-12">
      {/* Timeline dot */}
      <div className="absolute left-4 top-6 w-5 h-5 rounded-full bg-primary border-4 border-background z-10 flex items-center justify-center">
        <span className="text-[10px] font-bold text-primary-foreground">{stopNumber}</span>
      </div>

      <Card className="overflow-hidden">
        <CardHeader className="pb-2">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-lg truncate flex items-center gap-2">
                <MapPin className="w-4 h-4 text-primary shrink-0" />
                {stop.location_name}
                {bortleClass && <BortleBadge bortle={bortleClass} />}
              </h3>
              <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                <Calendar className="w-3.5 h-3.5" />
                <span>
                  {format(arrivalDate, 'MMM d')} - {format(departureDate, 'MMM d, yyyy')}
                </span>
                <span className="text-muted-foreground/50">•</span>
                <span>{totalNights} {totalNights === 1 ? 'night' : 'nights'}</span>
              </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <Button variant="ghost" size="icon" onClick={onEdit}>
                <Edit2 className="w-4 h-4" />
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-destructive">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete Stop</AlertDialogTitle>
                    <AlertDialogDescription>
                      Are you sure you want to remove "{stop.location_name}" from this trip?
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={onDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Visibility Summary */}
          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-2">
              <Star className="w-4 h-4 text-amber-500" />
              <span>{goodNights} of {totalNights} nights good for Milky Way</span>
            </div>
          </div>

          {/* Weather */}
          <TripStopWeather
            latitude={Number(stop.latitude)}
            longitude={Number(stop.longitude)}
            arrivalDate={stop.arrival_date}
            departureDate={stop.departure_date}
          />

          {/* Calendar Table - matches Milky Way Planner layout */}
          <TripCalendarTable nightsData={nightsData} />

          {/* Best Night Highlight */}
          {bestNight && bestNight.rating >= 5 && (
            <div className="bg-primary/5 border border-primary/20 rounded-lg p-3">
              <div className="flex items-center gap-2 text-sm flex-wrap">
                <Star className="w-4 h-4 text-primary fill-primary" />
                <span className="font-medium">Best night:</span>
                <span>{format(bestNight.date, 'EEEE, MMMM d')}</span>
                {bestNight.dayData && (
                  <>
                    <span className="text-muted-foreground">•</span>
                    <span className="text-muted-foreground">
                      Core visible {bestNight.dayData.galacticCoreStart} - {bestNight.dayData.galacticCoreEnd}
                    </span>
                    <span className="text-muted-foreground">•</span>
                    <span className={getQualityLabel(bestNightQuality).color}>
                      Quality: {bestNightQuality}/10
                    </span>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Notes */}
          {stop.notes && (
            <p className="text-sm text-muted-foreground italic">{stop.notes}</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
